"use client";

interface GTMPurchaseEventData {
  transaction_id: string;
  value: number;
  currency: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  items: GTMOrderItem[];
}

interface GTMPurchaseEvent {
  event: 'purchase';
  ecommerce: {
    transaction_id: string;
    value: number;
    currency: string;
    items: Array<{
      item_name: string;
      item_category?: string;
      price: number;
      quantity: number;
    }>;
  };
  user_data: {
    email: string;
    first_name: string;
    last_name: string;
    phone?: string;
    address?: {
      city?: string;
      postal_code?: string;
      country?: string;
    };
  };
}

interface GTMOrderItem {
  item_name: string;
  item_category?: string;
  price: number;
  quantity: number;
}

interface GTMRegistrationFunnelStepEvent {
  event: 'registration_funnel_step';
  step_name: 'service_selection';
  step_number: 1;
  page_url: string;
}

interface GTMDocumentUploadStepEvent {
  event: 'registration_funnel_step';
  step_name: 'document_upload';
  step_number: 2;
  page_url: string;
}

interface GTMOptionsStepEvent {
  event: 'registration_funnel_step';
  step_name: 'order_options';
  step_number: 3;
  page_url: string;
}

interface GTMPaymentStepEvent {
  event: 'registration_funnel_step';
  step_name: 'payment_info';
  step_number: 4;
  page_url: string;
}

interface GTMSignUpEvent {
  event: 'sign_up';
  method: string;
  user_data: {
    email: string;
    first_name: string;
    last_name: string;
  };
}

interface GTMAddPaymentInfoEvent {
  event: 'add_payment_info';
  ecommerce: {
    currency: string;
    value: number;
    payment_type: string;
    items: Array<{
      item_name: string;
      price: number;
      quantity: number;
    }>;
  };
}

/**
 * Helper function to generate items array for GTM based on delivery options
 */
const generateGTMItems = (
  translationType: string,
  delivery: string[] | undefined,
  orderTotal: number,
  pageCount?: number,
  wordCount?: number
): Array<{ item_name: string; price: number; quantity: number }> => {
  const items: Array<{ item_name: string; price: number; quantity: number }> = [];
  
  // Add main translation service
  items.push({
    item_name: translationType === 'certified' ? 'Certified Translation' : 'Standard Translation',
    price: orderTotal,
    quantity: 1
  });
  
  // Add delivery options if any
  if (delivery && delivery.length > 0) {
    const isCertified = translationType === 'certified';
    
    // Calculate pages for standard translation (word count / 250)
    const standardPages = wordCount ? Math.ceil(wordCount / 250) : (pageCount || 0);
    
    // Delivery rates based on translation type. Kept in sync with
    // DELIVERY_RATES_STANDARD / DELIVERY_RATES_CERTIFIED in Checkout
    // constants so GA4 revenue attribution matches what we actually
    // charge. Notarization at $19.95 per PROD-2026-05-18-007.
    const deliveryRates: Record<string, number> = {
      // Standard translation rates (per page equivalent — note the
      // checkout now charges per-word past the 250-word floor, but
      // these analytics-only constants stay aggregate-friendly).
      'expeditedDelivery': 10,
      'desktopPublishing': 10,
      'proofreading': 12.5,
      // Certified translation rates (per page or flat fee)
      'notarization': 19.95,
      'mailHardCopy': 20,
      'expeditedTurnaround': 10, // per page for certified
    };
    
    const deliveryLabels: Record<string, string> = {
      'expeditedDelivery': 'Expedited Delivery',
      'desktopPublishing': 'Desktop Publishing', 
      'proofreading': 'Proofreading',
      'notarization': 'Notarization',
      'mailHardCopy': 'Mailed Hard Copy',
      'expeditedTurnaround': 'Expedited Turnaround'
    };
    
    delivery.forEach(option => {
      const label = deliveryLabels[option];
      if (label) {
        let price = 0;
        
        if (option === 'expeditedTurnaround' && isCertified) {
          // Certified expedited turnaround is per page
          price = deliveryRates[option] * (pageCount || 0);
        } else if (!isCertified && ['expeditedDelivery', 'desktopPublishing', 'proofreading'].includes(option)) {
          // Standard translation options are per page equivalent
          price = deliveryRates[option] * standardPages;
        } else if (isCertified && ['notarization', 'mailHardCopy'].includes(option)) {
          // Certified flat fee options
          price = deliveryRates[option];
        }
        
        items.push({
          item_name: label,
          price: price,
          quantity: 1
        });
      }
    });
  }
  
  return items;
};

/**
 * Pushes a purchase event to Google Tag Manager's data layer
 * Includes deduplication mechanism to prevent duplicate events for the same transaction
 */
export const pushPurchaseEvent = (data: GTMPurchaseEventData): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];

  // Deduplication mechanism using sessionStorage
  const storageKey = `gtm_purchase_${data.transaction_id}`;
  const hasAlreadyFired = sessionStorage.getItem(storageKey);

  if (hasAlreadyFired) {
    return;
  }

  const eventData: GTMPurchaseEvent = {
    event: 'purchase',
    ecommerce: {
      transaction_id: data.transaction_id,
      value: data.value,
      currency: data.currency,
      items: data.items
    },
    user_data: {
      email: data.email,
      first_name: data.first_name,
      last_name: data.last_name,
      phone: data.phone,
      address: {
        city: data.city,
        postal_code: data.postal_code,
        country: data.country
      }
    }
  };

  // Push to dataLayer
  window.dataLayer.push(eventData);

  // Mark this transaction as processed
  sessionStorage.setItem(storageKey, 'true');
};

/**
 * Pushes a registration funnel step event to Google Tag Manager's data layer
 * This event fires automatically when visitor arrives at the service selection step
 */
export const pushRegistrationFunnelStepEvent = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];

  const eventData: GTMRegistrationFunnelStepEvent = {
    event: 'registration_funnel_step',
    step_name: 'service_selection',
    step_number: 1,
    page_url: window.location.href
  };

  // Push to dataLayer
  window.dataLayer.push(eventData);
};

/**
 * Pushes a document upload step event to Google Tag Manager's data layer
 * This event fires automatically when visitor arrives at the document upload step
 */
export const pushDocumentUploadStepEvent = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];

  const eventData: GTMDocumentUploadStepEvent = {
    event: 'registration_funnel_step',
    step_name: 'document_upload',
    step_number: 2,
    page_url: window.location.href
  };

  // Push to dataLayer
  window.dataLayer.push(eventData);
};

/**
 * Pushes an options step event to Google Tag Manager's data layer
 * This event fires automatically when visitor arrives at the options step
 */
export const pushOptionsStepEvent = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];

  const eventData: GTMOptionsStepEvent = {
    event: 'registration_funnel_step',
    step_name: 'order_options',
    step_number: 3,
    page_url: window.location.href
  };

  // Push to dataLayer
  window.dataLayer.push(eventData);
};

/**
 * Pushes a payment step event to Google Tag Manager's data layer
 * This event fires automatically when visitor arrives at the payment step
 */
export const pushPaymentStepEvent = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];

  const eventData: GTMPaymentStepEvent = {
    event: 'registration_funnel_step',
    step_name: 'payment_info',
    step_number: 4,
    page_url: window.location.href
  };

  // Push to dataLayer
  window.dataLayer.push(eventData);
};

/**
 * Clears the deduplication flag for a specific transaction
 * Useful for testing or if you need to re-fire the event
 */
export const clearPurchaseEventFlag = (transactionId: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const storageKey = `gtm_purchase_${transactionId}`;
  sessionStorage.removeItem(storageKey);
}

/**
 * Pushes a sign_up event to Google Tag Manager's data layer
 * This event fires when a user successfully creates a new account
 * Includes deduplication mechanism to prevent duplicate events for the same email
 */
export const pushSignUpEvent = (data: { email: string; first_name: string; last_name: string }): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];

  // Deduplication mechanism using sessionStorage based on email
  const storageKey = `gtm_signup_${data.email}`;
  const hasAlreadyFired = sessionStorage.getItem(storageKey);

  if (hasAlreadyFired) {
    return;
  }

  const eventData: GTMSignUpEvent = {
    event: 'sign_up',
    method: 'email',
    user_data: {
      email: data.email,
      first_name: data.first_name,
      last_name: data.last_name,
    }
  };

  // Push to dataLayer
  window.dataLayer.push(eventData);

  // Mark this email as processed
  sessionStorage.setItem(storageKey, 'true');
};

/**
 * Clears the deduplication flag for a specific email sign up
 * Useful for testing or if you need to re-fire the event
 */
export const clearSignUpEventFlag = (email: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const storageKey = `gtm_signup_${email}`;
  sessionStorage.removeItem(storageKey);
}

/**
 * Pushes an add_payment_info event to Google Tag Manager's data layer
 * This event fires when user first interacts with payment input fields
 * Includes deduplication mechanism to prevent duplicate events per session
 */
export const pushAddPaymentInfoEvent = (data: {
  payment_type: string;
  value: number;
  currency: string;
  translationType?: string;
  delivery?: string[];
  pageCount?: number;
  wordCount?: number;
}): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // Initialize dataLayer if it doesn't exist
  window.dataLayer = window.dataLayer || [];

  // Deduplication mechanism using sessionStorage - only fire once per session
  const storageKey = 'gtm_add_payment_info';
  const hasAlreadyFired = sessionStorage.getItem(storageKey);

  if (hasAlreadyFired) {
    return;
  }

  const items = generateGTMItems(
    data.translationType || 'standard',
    data.delivery,
    data.value,
    data.pageCount,
    data.wordCount
  );

  const eventData: GTMAddPaymentInfoEvent = {
    event: 'add_payment_info',
    ecommerce: {
      currency: data.currency,
      value: data.value,
      payment_type: data.payment_type,
      items: items
    }
  };

  // Push to dataLayer
  window.dataLayer.push(eventData);

  // Mark this event as processed for the session
  sessionStorage.setItem(storageKey, 'true');
};

/**
 * Clears the deduplication flag for add_payment_info event
 * Useful for testing or if you need to re-fire the event
 */
export const clearAddPaymentInfoEventFlag = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  sessionStorage.removeItem('gtm_add_payment_info');
}

/**
 * Type declaration for the dataLayer
 */
declare global {
  interface Window {
    dataLayer?: Object[];
  }
}
